/**
 * Reads the finished pattern's dots and connecting lines out of its SVG, so the end
 * screen can draw them as sparks. Regex rather than DOM, so it runs in tests too.
 */

export interface PatternPoint {
  x: number;
  y: number;
  /** Dot radius in SVG units; the pattern has small and large dots. */
  r: number;
}

export interface Pattern {
  width: number;
  height: number;
  points: PatternPoint[];
  /** Pairs of `points` indices joined by a line. */
  links: [number, number][];
}

function attr(tag: string, name: string): string | undefined {
  return new RegExp(`\\s${name}="([^"]*)"`).exec(tag)?.[1];
}

function numberAttr(tag: string, name: string): number {
  const value = Number(attr(tag, name));
  if (!Number.isFinite(value)) throw new Error(`[pattern] bad ${name} in ${tag}`);
  return value;
}

export function parsePattern(svg: string): Pattern {
  const viewBox = /viewBox="([^"]*)"/
    .exec(svg)?.[1]
    ?.trim()
    .split(/[\s,]+/)
    .map(Number);
  if (!viewBox || viewBox.length !== 4) throw new Error("[pattern] missing viewBox");

  const indexById = new Map<string, number>();
  const points: PatternPoint[] = [];
  for (const [tag] of svg.matchAll(/<circle\b[^>]*>/g)) {
    const id = attr(tag, "id");
    if (id) indexById.set(id, points.length);
    points.push({ x: numberAttr(tag, "cx"), y: numberAttr(tag, "cy"), r: numberAttr(tag, "r") });
  }
  if (points.length === 0) throw new Error("[pattern] no circles found");

  const links: [number, number][] = [];
  for (const [tag] of svg.matchAll(/<line\b[^>]*>/g)) {
    const ends = /^l-(\w+)-(\w+)$/.exec(attr(tag, "id") ?? "");
    const a = ends && indexById.get(ends[1]!);
    const b = ends && indexById.get(ends[2]!);
    if (a == null || b == null) throw new Error(`[pattern] line without known ends: ${tag}`);
    links.push([a, b]);
  }

  return { width: viewBox[2]!, height: viewBox[3]!, points, links };
}
